/**
 * Provisional Tax — dedicated screen for each client's three provisional
 * instalments per year. Reuses Job rows with kind=provisional_tax; every
 * instalment carries `installment_year` (income year) and `installment_number`
 * (1|2|3). One-tap "Send payment slip" fires the tax_provisional_reminder
 * template to the client. "Mark paid" closes the row.
 *
 * NZ standard method:  P1 = 28 Aug,  P2 = 15 Jan,  P3 = 7 May
 *                      (relative to income year ending 31 March)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw, Mail, CheckCircle2, Plus, DollarSign, X, Undo2, AlertTriangle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const INSTALMENT_LABELS = { 1: "P1", 2: "P2", 3: "P3" };
const INSTALMENT_MONTHS = { 1: "28 Aug", 2: "15 Jan", 3: "7 May" };

function fmt(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso.slice(0, 10); }
}

function currentIncomeYear() {
    const t = new Date();
    return t.getMonth() >= 3 ? t.getFullYear() + 1 : t.getFullYear();
}

function daysOutTone(d, paid) {
    if (paid) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (d === null || d === undefined) return "bg-zinc-100 text-zinc-600 border-zinc-200";
    if (d < 0) return "bg-red-100 text-red-800 border-red-200";
    if (d <= 14) return "bg-amber-100 text-amber-900 border-amber-200";
    return "bg-sky-50 text-sky-800 border-sky-200";
}

function daysOutLabel(d, paid) {
    if (paid) return "Paid";
    if (d === null || d === undefined) return "No date";
    if (d < 0) return `${Math.abs(d)}d overdue`;
    if (d === 0) return "Due today";
    return `${d}d left`;
}

export default function ProvisionalTax() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [groups, setGroups] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [year, setYear] = useState(currentIncomeYear());
    const [paidFilter, setPaidFilter] = useState("all");
    const [q, setQ] = useState("");
    const [genOpen, setGenOpen] = useState(false);
    const [payOpen, setPayOpen] = useState(null);   // instalment row
    const [amtOpen, setAmtOpen] = useState(null);   // instalment row

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("income_year", String(year));
            if (paidFilter !== "all") params.set("paid", paidFilter);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await api.get(`/tax/provisional/?${params.toString()}`);
            setGroups(data.groups || []);
            setSummary(data.summary || {});
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [year, paidFilter, q]);

    useEffect(() => { load(); }, [load]);

    async function sendSlip(row) {
        setBusyId(`slip-${row.id}`);
        try {
            await api.post(`/tax/provisional/${row.id}/send-slip`, {});
            toast.success(`Payment slip emailed to ${row.client_name}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function markPaid(row, amount, reference) {
        setBusyId(`pay-${row.id}`);
        try {
            await api.post(`/tax/provisional/${row.id}/mark-paid`, {
                paid_amount: amount ? Number(amount) : null,
                reference: reference || null,
            });
            toast.success(`${INSTALMENT_LABELS[row.installment_number]} marked as paid`);
            setPayOpen(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function unpay(row) {
        if (!window.confirm(`Undo payment on ${INSTALMENT_LABELS[row.installment_number]} for ${row.client_name}?`)) return;
        setBusyId(`unpay-${row.id}`);
        try {
            await api.post(`/tax/provisional/${row.id}/unpay`, {});
            toast.success("Payment undone");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    async function updateAmount(row, ritAmount, perInstalment) {
        setBusyId(`amt-${row.id}`);
        try {
            const body = {};
            if (ritAmount !== "") body.rit_amount = Number(ritAmount);
            else if (perInstalment !== "") body.amount = Number(perInstalment);
            await api.patch(`/tax/provisional/${row.id}`, body);
            toast.success("Amount updated");
            setAmtOpen(null);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusyId(null); }
    }

    const yearOptions = useMemo(() => {
        const cy = currentIncomeYear();
        return [cy + 1, cy, cy - 1, cy - 2];
    }, []);

    return (
        <div className="space-y-4">
            <header className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-semibold text-[#0F1115] tracking-tight">Provisional tax</h1>
                    <p className="text-sm text-[#5E636E] mt-0.5">
                        Three instalments per client — {INSTALMENT_MONTHS[1]}, {INSTALMENT_MONTHS[2]}, {INSTALMENT_MONTHS[3]}. Tap Send slip to email a client their payment details.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={load} disabled={loading} data-testid="prov-refresh">
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCcw className="h-4 w-4 mr-1" /> Refresh</>}
                    </Button>
                    <Button onClick={() => setGenOpen(true)} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="prov-generate-open">
                        <Plus className="h-4 w-4 mr-1" /> Generate instalments
                    </Button>
                </div>
            </header>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label="Instalments" value={summary.total || 0} tone="text-slate-800 bg-slate-50 border-slate-200" />
                <Kpi label="Clients" value={summary.clients || 0} tone="text-[#0F1115] bg-white border-[#E4E4E7]" />
                <Kpi label="Paid" value={summary.paid || 0} tone="text-emerald-800 bg-emerald-50 border-emerald-200" icon={CheckCircle2} />
                <Kpi label="Due ≤ 14 days" value={summary.due_soon || 0} tone="text-amber-800 bg-amber-50 border-amber-200" />
                <Kpi label="Overdue" value={summary.overdue || 0} tone="text-red-700 bg-red-50 border-red-200" icon={AlertTriangle} />
            </div>

            <Card className="p-3 flex flex-wrap items-center gap-2">
                <Label className="text-[12px] text-[#5E636E]">Income year</Label>
                <select
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                    className="h-8 rounded-md border border-[#E4E4E7] bg-white text-[13px] px-2"
                    data-testid="prov-year"
                >
                    {yearOptions.map((y) => <option key={y} value={y}>Year ending 31 Mar {y}</option>)}
                </select>
                <div className="flex gap-1 ml-2" data-testid="prov-paid-tabs">
                    {[
                        { key: "all", label: "All" },
                        { key: "unpaid", label: "Unpaid" },
                        { key: "paid", label: "Paid" },
                        { key: "overdue", label: "Overdue" },
                    ].map((t) => (
                        <button
                            key={t.key}
                            onClick={() => setPaidFilter(t.key)}
                            className={`h-8 rounded-full border px-3 text-[12.5px] ${paidFilter === t.key ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50"}`}
                            data-testid={`prov-paid-${t.key}`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <Input
                    placeholder="Search client…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    className="max-w-xs h-8 ml-auto"
                    data-testid="prov-search"
                />
            </Card>

            <Card className="p-0 overflow-hidden">
                {loading ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading provisional tax…</div>
                ) : groups.length === 0 ? (
                    <div className="p-12 text-center text-sm text-[#5E636E]" data-testid="prov-empty">
                        No provisional instalments for year ending 31 Mar {year}. Tap <b>Generate instalments</b> to seed a client's three payments.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-[13px]" data-testid="prov-table">
                            <thead className="bg-[#FAFAF7] text-[11.5px] uppercase tracking-wider text-[#5E636E]">
                                <tr>
                                    <th className="text-left px-3 py-2 font-medium">Client</th>
                                    <th className="text-left px-3 py-2 font-medium">P1 · {INSTALMENT_MONTHS[1]}</th>
                                    <th className="text-left px-3 py-2 font-medium">P2 · {INSTALMENT_MONTHS[2]}</th>
                                    <th className="text-left px-3 py-2 font-medium">P3 · {INSTALMENT_MONTHS[3]}</th>
                                    <th className="text-right px-3 py-2 font-medium">RIT / Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => {
                                    const total = (g.installments || []).reduce((s, r) => s + (r?.amount_value || 0), 0);
                                    return (
                                        <tr key={`${g.client_id}-${g.income_year}`} className="border-t border-[#EEEDE7] align-top" data-testid={`prov-group-${g.client_id}`}>
                                            <td className="px-3 py-3">
                                                <div className="font-medium text-[#0F1115]">{g.client_name || "—"}</div>
                                                <div className="text-[11px] text-[#5E636E] mt-0.5">Year ending 31 Mar {g.income_year}</div>
                                                {g.paid_count > 0 && <div className="text-[11px] text-emerald-700 mt-0.5">{g.paid_count}/3 paid</div>}
                                                {g.overdue_count > 0 && <div className="text-[11px] text-red-700 mt-0.5">{g.overdue_count} overdue</div>}
                                            </td>
                                            {g.installments.map((r, idx) => (
                                                <td key={idx} className="px-3 py-3 align-top">
                                                    {!r ? (
                                                        <span className="text-[12px] text-[#5E636E]">—</span>
                                                    ) : (
                                                        <div className="space-y-1.5">
                                                            <div className="flex items-center gap-1.5">
                                                                <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10.5px] font-medium ${daysOutTone(r.days_out, r.is_paid)}`}>
                                                                    {daysOutLabel(r.days_out, r.is_paid)}
                                                                </span>
                                                                <span className="text-[11px] text-[#5E636E]">{fmt(r.filing_deadline)}</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => setAmtOpen(r)}
                                                                className="text-[15px] tabular-nums font-semibold text-[#0F1115] hover:underline"
                                                                data-testid={`prov-amt-${r.id}`}
                                                                title="Click to edit amount"
                                                            >
                                                                {r.amount_display || "Set amount"}
                                                            </button>
                                                            {r.cadence_count > 0 && (
                                                                <div className="text-[10.5px] text-emerald-700">
                                                                    Slip sent · {r.cadence_count} email{r.cadence_count === 1 ? "" : "s"}
                                                                </div>
                                                            )}
                                                            {r.is_paid ? (
                                                                <div className="flex flex-wrap gap-1">
                                                                    <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-800">
                                                                        <CheckCircle2 className="h-3 w-3" /> Paid {fmt(r.paid_at)}
                                                                    </span>
                                                                    {isAdmin && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => unpay(r)}
                                                                            className="text-[10.5px] text-[#5E636E] hover:text-red-700 inline-flex items-center gap-0.5"
                                                                            data-testid={`prov-unpay-${r.id}`}
                                                                        >
                                                                            <Undo2 className="h-2.5 w-2.5" /> undo
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-1 pt-1">
                                                                    <Button
                                                                        size="sm" variant="outline"
                                                                        onClick={() => sendSlip(r)}
                                                                        disabled={busyId === `slip-${r.id}` || !r.amount_display}
                                                                        className="h-7 text-[11px] px-2"
                                                                        data-testid={`prov-slip-${r.id}`}
                                                                        title="Email the client their payment details"
                                                                    >
                                                                        {busyId === `slip-${r.id}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Mail className="h-3 w-3 mr-1" /> Send slip</>}
                                                                    </Button>
                                                                    <Button
                                                                        size="sm"
                                                                        onClick={() => setPayOpen(r)}
                                                                        className="h-7 text-[11px] px-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                                                        data-testid={`prov-pay-${r.id}`}
                                                                    >
                                                                        <CheckCircle2 className="h-3 w-3 mr-1" /> Mark paid
                                                                    </Button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </td>
                                            ))}
                                            <td className="px-3 py-3 text-right align-top">
                                                <div className="text-[15px] font-semibold tabular-nums">
                                                    {total ? `NZ$${total.toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                                                </div>
                                                {g.rit_amount ? (
                                                    <div className="text-[11px] text-[#5E636E] mt-1">RIT NZ${Number(g.rit_amount).toLocaleString("en-NZ")}</div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <GenerateDialog open={genOpen} onClose={() => setGenOpen(false)} defaultYear={year} onDone={load} />
            <MarkPaidDialog row={payOpen} onClose={() => setPayOpen(null)} onConfirm={markPaid} busy={busyId?.startsWith("pay-")} />
            <EditAmountDialog row={amtOpen} onClose={() => setAmtOpen(null)} onConfirm={updateAmount} busy={busyId?.startsWith("amt-")} />
        </div>
    );
}

function Kpi({ label, value, tone, icon: Icon }) {
    return (
        <div className={`rounded-xl border p-3 ${tone}`}>
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-wider opacity-80">{label}</span>
                {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
            </div>
            <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        </div>
    );
}

function GenerateDialog({ open, onClose, defaultYear, onDone }) {
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState("");
    const [rit, setRit] = useState("");
    const [overrideAmt, setOverrideAmt] = useState("");
    const [income, setIncome] = useState(defaultYear);
    const [overwrite, setOverwrite] = useState(false);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");

    useEffect(() => {
        if (!open) return;
        setIncome(defaultYear);
        (async () => {
            try {
                const { data } = await api.get("/clients/");
                setClients(Array.isArray(data) ? data : data?.rows || []);
            } catch (e) { toast.error(formatApiError(e)); }
        })();
    }, [open, defaultYear]);

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return clients.slice(0, 100);
        return clients.filter((c) => (c.name || "").toLowerCase().includes(term)).slice(0, 100);
    }, [clients, q]);

    async function submit() {
        if (!clientId) { toast.error("Pick a client first"); return; }
        setBusy(true);
        try {
            await api.post("/tax/provisional/generate", {
                client_id: clientId,
                income_year: Number(income),
                rit_amount: rit ? Number(rit) : null,
                amount_override: overrideAmt ? Number(overrideAmt) : null,
                overwrite,
            });
            toast.success("Instalments generated");
            setClientId(""); setRit(""); setOverrideAmt(""); setOverwrite(false); setQ("");
            onClose();
            onDone?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-lg" data-testid="prov-gen-dialog">
                <DialogHeader>
                    <DialogTitle>Generate provisional instalments</DialogTitle>
                    <DialogDescription>
                        Creates three payments (P1 · 28 Aug, P2 · 15 Jan, P3 · 7 May). Uplift = RIT × 105% ÷ 3.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">Client</Label>
                        <Input placeholder="Search client…" value={q} onChange={(e) => setQ(e.target.value)} className="mt-1" data-testid="prov-gen-search" />
                        <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-[#E4E4E7]">
                            {filtered.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    onClick={() => setClientId(c.id)}
                                    className={`w-full text-left px-3 py-1.5 text-[13px] hover:bg-zinc-50 ${clientId === c.id ? "bg-[#0F1115] text-white hover:bg-[#0F1115]" : ""}`}
                                    data-testid={`prov-gen-client-${c.id}`}
                                >
                                    {c.name}
                                </button>
                            ))}
                            {filtered.length === 0 && <div className="p-3 text-[12px] text-[#5E636E]">No matches</div>}
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">Income year ending 31 Mar</Label>
                            <Input type="number" value={income} onChange={(e) => setIncome(Number(e.target.value))} className="mt-1" data-testid="prov-gen-year" />
                        </div>
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">Prior-year RIT (optional)</Label>
                            <Input type="number" step="0.01" placeholder="e.g. 12000" value={rit} onChange={(e) => setRit(e.target.value)} className="mt-1" data-testid="prov-gen-rit" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">Or set total year amount (optional)</Label>
                        <Input type="number" step="0.01" placeholder="e.g. 15000 — split 3 ways" value={overrideAmt} onChange={(e) => setOverrideAmt(e.target.value)} className="mt-1" data-testid="prov-gen-override" />
                    </div>
                    <label className="flex items-center gap-2 text-[12px] text-[#5E636E]">
                        <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="prov-gen-overwrite" />
                        Overwrite existing instalments for this year (if any)
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={submit} disabled={busy || !clientId} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="prov-gen-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MarkPaidDialog({ row, onClose, onConfirm, busy }) {
    const [amount, setAmount] = useState("");
    const [reference, setReference] = useState("");
    useEffect(() => { if (row) { setAmount(row.amount_value || ""); setReference(""); } }, [row]);
    if (!row) return null;
    return (
        <Dialog open={!!row} onOpenChange={onClose}>
            <DialogContent data-testid="prov-pay-dialog">
                <DialogHeader>
                    <DialogTitle>Mark {INSTALMENT_LABELS[row.installment_number]} paid</DialogTitle>
                    <DialogDescription>
                        {row.client_name} — due {fmt(row.filing_deadline)}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">Amount paid (NZD)</Label>
                        <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" data-testid="prov-pay-amount" />
                    </div>
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">IRD reference / receipt (optional)</Label>
                        <Input value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1" data-testid="prov-pay-ref" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={() => onConfirm(row, amount, reference)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="prov-pay-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Mark paid</>}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function EditAmountDialog({ row, onClose, onConfirm, busy }) {
    const [rit, setRit] = useState("");
    const [per, setPer] = useState("");
    useEffect(() => { if (row) { setRit(""); setPer(row.amount_value || ""); } }, [row]);
    if (!row) return null;
    return (
        <Dialog open={!!row} onOpenChange={onClose}>
            <DialogContent data-testid="prov-amt-dialog">
                <DialogHeader>
                    <DialogTitle>Edit amount · {INSTALMENT_LABELS[row.installment_number]}</DialogTitle>
                    <DialogDescription>
                        Set an RIT to re-uplift all three instalments, or override just this one.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">Prior-year RIT (re-splits all three)</Label>
                        <Input type="number" step="0.01" value={rit} onChange={(e) => { setRit(e.target.value); if (e.target.value) setPer(""); }} className="mt-1" data-testid="prov-amt-rit" />
                    </div>
                    <div className="text-center text-[11px] text-[#5E636E]">— or —</div>
                    <div>
                        <Label className="text-[12px] text-[#5E636E]">This instalment only (NZD)</Label>
                        <Input type="number" step="0.01" value={per} onChange={(e) => { setPer(e.target.value); if (e.target.value) setRit(""); }} className="mt-1" data-testid="prov-amt-per" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button onClick={() => onConfirm(row, rit, per)} disabled={busy || (!rit && !per)} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="prov-amt-submit">
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
